import { FC, forwardRef, Fragment, useEffect, useMemo, useState } from "react";
import { Button, SearchAdvanced, SelectAdvanced, Tag } from "@crowd/ui-kit";
import { useSelector } from "react-redux";
import classNames from "classnames";
import styles from "./IdeasFilter.module.scss";
import EventsTrackWrapperClick from "../../../containers/EventsTrack/wrappers/EventsTrackWrapperClick";
import EventsTrackWrapperScroll from "../../../containers/EventsTrack/wrappers/EventsTrackWrapperScroll";
import { selectDistricts } from "../../../../store/selectors/address";
import { selectIdeasTags } from "../../../../store/selectors/idea";
import { District } from "../../../../types/Address.interface";
import { ArrayItem } from "../../../../types/Common.interface";
import { useIsMobile } from "../../../../hooks/useMedia";
import AppModal from "../../../containers/Modals/AppModal/AppModal";
import { selectListMaxHeight } from "../../../../utils/constants";

export interface Filter {
  search: string;
  district: District | null;
  tags: string[] | null;
  type: ArrayItem<typeof typeList> | null;
  sort: ArrayItem<typeof sortList> | null;
}

export interface IdeasFilterProps {
  filter: Filter;
  onChange: (filter: Filter) => void;
  isDisabled?: boolean;
}

const typeList = [
  {
    value: "photo",
    text: "Фото",
  },
  {
    value: "video",
    text: "Видео",
  },
];

const sortList = [
  {
    value: "created",
    text: "По новизне",
  },
  {
    value: "likes",
    text: "По популярности",
  },
];

const SearchInput = forwardRef<HTMLInputElement, any>((props, ref) => (
  <EventsTrackWrapperClick
    id={["FILTER", "FIELD", "CLICK"]}
    replace={["Поиск"]}
  >
    <input ref={ref} {...props} />
  </EventsTrackWrapperClick>
));

const IdeasFilter: FC<IdeasFilterProps> = ({
  filter,
  onChange,
  isDisabled = false,
}) => {
  const isPhone = useIsMobile();

  const districts = useSelector(selectDistricts);
  const ideasTags = useSelector(selectIdeasTags);

  const [phoneFilterDraft, setPhoneFilterDraft] = useState<Filter>();

  const tagsGroups = useMemo(
    () =>
      ideasTags?.map(({ id, name, tags }) => ({
        id,
        title: name,
        items: tags.map((t) => t.name),
      })),
    [ideasTags]
  );

  useEffect(() => {
    !isPhone && setPhoneFilterDraft(undefined);
  }, [isPhone]);

  const renderPartialFilter = (
    filterValue: Filter,
    changeFn: IdeasFilterProps["onChange"]
  ) => (
    <>
      {/* <SelectAdvanced
        items={districts || []}
        value={filterValue.district}
        label="Район"
        bindTo="name"
        onItemSelect={(newValue) =>
          changeFn({ ...filterValue, district: newValue })}
        isInnerLabel
        className={styles["filter-selectors__district"]}
        listWidth={!isPhone ? 278 : undefined}
        isDisabled={isDisabled}
        sortByAlphabet
        markSelected
        maxListHeight={selectListMaxHeight}
        listPosition="bottom"
      /> */}
      <SelectAdvanced
        items={tagsGroups}
        value={filterValue.tags}
        buttonTitle="Теги"
        hideClearIcon
        onItemSelect={(newValue) =>
          changeFn({
            ...filterValue,
            tags: newValue?.length ? newValue : null,
          })}
        isMultiple
        asTagListType="group"
        className={styles["filter-selectors__tags"]}
        listWidth={!isPhone ? 278 : undefined}
        isDisabled={isDisabled}
        maxListHeight={selectListMaxHeight}
        listPosition="bottom"
      />
      <SelectAdvanced
        items={typeList}
        value={filterValue.type}
        label="Тип"
        bindTo="text"
        onItemSelect={(newValue) =>
          changeFn({ ...filterValue, type: newValue })}
        isInnerLabel
        className={styles["filter-selectors__type"]}
        isDisabled={isDisabled}
        markSelected
        maxListHeight={selectListMaxHeight}
        listPosition="bottom"
      />
    </>
  );

  const renderFilter = () => {
    const phoneFilterNotEmpty = filter.district || filter.tags || filter.type;

    return (
      <div className={styles.filter}>
        <SearchAdvanced
          value={filter.search}
          placeholder={
            isPhone
              ? "Поиск по идее, адресу, автору"
              : "Поиск по названию идеи, адресу, автору"
          }
          onChange={(newValue) => onChange({ ...filter, search: newValue })}
          autocomplete={false}
          ComponentInput={SearchInput}
        />
        <div className={styles["filter-selectors"]}>
          {isPhone ? (
            <Button
              text=""
              size="l"
              type={phoneFilterNotEmpty ? "outlined" : "outlined-grey"}
              icon={(
                <span
                  className={classNames(
                    styles["filter-btn__icon"],
                    "promo-icon-67"
                  )}
                />
              )}
              classNames={classNames(styles["filter-btn"], {
                [styles["filter-btn_empty"]]: !phoneFilterNotEmpty,
              })}
              onClick={() => setPhoneFilterDraft({ ...filter })}
            />
          ) : (
            renderPartialFilter(filter, onChange)
          )}
          <SelectAdvanced
            items={sortList}
            value={filter.sort}
            label="Сортировка"
            bindTo="text"
            onItemSelect={(newValue) => onChange({ ...filter, sort: newValue })}
            isInnerLabel
            className={styles["filter-selectors__sort"]}
            isDisabled={isDisabled}
            markSelected
            maxListHeight={selectListMaxHeight}
            listPosition="bottom"
          />
        </div>
      </div>
    );
  };

  const renderTags = (
    filterValue: Filter,
    changeFn: IdeasFilterProps["onChange"]
  ) => {
    if (!filterValue.tags?.length) {
      return;
    }

    const deleteTag = (tag: string) => {
      const newTagsList = filterValue.tags.filter((t) => t !== tag);
      changeFn({
        ...filterValue,
        tags: newTagsList.length ? newTagsList : null,
      });
    };

    return (
      <div className={styles.tags}>
        {filterValue.tags.map((item, i) => (
          <Tag key={i} withRemoving onRemove={() => deleteTag(item)}>
            {item}
          </Tag>
        ))}
      </div>
    );
  };

  return (
    !!ideasTags &&
    !!districts && (
      <>
        <EventsTrackWrapperScroll id={["FILTER", "DOCSCROLL"]}>
          <div className={styles.wrapper}>
            {renderFilter()}
            {!isPhone && renderTags(filter, onChange)}
          </div>
        </EventsTrackWrapperScroll>

        {!!phoneFilterDraft && (
          <AppModal isOpen onClose={() => setPhoneFilterDraft(undefined)}>
            <Fragment key="body">
              <div className={styles["filter-partial"]}>
                {renderPartialFilter(phoneFilterDraft, setPhoneFilterDraft)}
                {renderTags(phoneFilterDraft, setPhoneFilterDraft)}
              </div>
            </Fragment>
            <Fragment key="footer">
              <Button
                size="l"
                type="outlined-grey"
                text="Сбросить"
                onClick={() =>
                  setPhoneFilterDraft((prev) => ({
                    ...prev,
                    district: null,
                    tags: null,
                    type: null,
                  }))}
              />
              <Button
                size="l"
                type="filled"
                text="Применить"
                onClick={() => {
                  onChange(phoneFilterDraft);
                  setPhoneFilterDraft(undefined);
                }}
              />
            </Fragment>
          </AppModal>
        )}
      </>
    )
  );
};

export default IdeasFilter;
